import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api from "../../services/api";
import { getApiBaseUrl } from "../../services/apiBase.js";
import Layout from "../../components/Layout/Layout";
import "./AgentInteraction.css";

const AgentInteraction = () => {
  const { agentType } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { userId, userType, context } = location.state || {};

  const [history, setHistory] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [connected, setConnected] = useState(false);
  const socketRef = useRef(null);
  const sessionStartRef = useRef(null);
  const interactionCountRef = useRef(0);

  // Get API URL from environment or use default
  const API_BASE_URL = getApiBaseUrl();
  const WS_BASE_URL = API_BASE_URL.replace("http", "ws");

  // Configuration for different agent types
  const agentConfig = {
    tutor: {
      title: "AI Tutor",
      placeholder: "Ask your study question...",
      icon: "fas fa-chalkboard-teacher",
    },
    quiz: {
      title: "Quiz Assistant",
      placeholder: "Get quiz recommendations...",
      icon: "fas fa-tasks",
    },
    feedback: {
      title: "Feedback Agent",
      placeholder: "Request feedback or generate student assessments...",
      icon: "fas fa-comments",
    },
    analytics: {
      title: "Analytics Agent",
      placeholder: "Ask about student performance and trends...",
      icon: "fas fa-chart-bar",
    },
    teacher_support: {
      title: "Teacher Support",
      placeholder: "Ask for teaching strategies or resources...",
      icon: "fas fa-user-graduate",
    },
    lesson_planner: {
      title: "Lesson Planner",
      placeholder: "Describe the lesson you need help planning...",
      icon: "fas fa-book",
    },
    curriculum: {
      title: "Curriculum Viewer",
      placeholder: "Ask about curriculum content or standards...",
      icon: "fas fa-search",
    },
  };

  const currentAgent = agentConfig[agentType] || {
    title: "AI Agent",
    placeholder: "Type your message...",
    icon: "fas fa-robot",
  };

  // Function to track agent usage
  const trackAgentUsage = async (action, data = {}) => {
    try {
      const token =
        localStorage.getItem("access_token") ||
        sessionStorage.getItem("access_token");
      const studentId = localStorage.getItem("student_id");

      if (!token || !studentId) return;

      const payload = {
        student_id: studentId,
        agent_type: agentType,
        subject: context?.subject || null,
        topic: context?.topic || null,
        action: action,
        ...data,
      };

      await fetch(`${API_BASE_URL}/api/agent-usage/track`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
    } catch (error) {
      console.error("Error tracking agent usage:", error);
    }
  };

  // Set up WebSocket connection on component mount
  useEffect(() => {
    // Record session start time
    sessionStartRef.current = new Date();

    // Track session start
    trackAgentUsage("session_start", {
      session_start: sessionStartRef.current.toISOString(),
    });

    // Connect to WebSocket for the agent
    const socket = new WebSocket(
      `${WS_BASE_URL}/api/ws/${agentType}/${userId}`
    );

    socket.onopen = () => {
      console.log("WebSocket connected");
      setConnected(true);

      // Send initial context
      const contextMessage = JSON.stringify({
        type: "context",
        userId: userId,
        userType: userType,
        context: context || {},
      });

      socket.send(contextMessage);

      // Add welcome message based on agent type
      setHistory([
        {
          type: "agent",
          message: `Welcome to the ${currentAgent.title}! How can I help you today?`,
          timestamp: new Date(),
        },
      ]);
    };

    socket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      setHistory((prev) => [
        ...prev,
        {
          type: "agent",
          message: data.content || data.response || "No response",
          timestamp: new Date(),
        },
      ]);
      setLoading(false);

      // Track interaction completion
      interactionCountRef.current += 1;
    };

    socket.onerror = (error) => {
      console.error("WebSocket error:", error);
      setHistory((prev) => [
        ...prev,
        {
          type: "error",
          message: "Connection error. Please try refreshing the page.",
          timestamp: new Date(),
        },
      ]);
      setConnected(false);
      setLoading(false);
    };

    socket.onclose = () => {
      console.log("WebSocket disconnected");
      setConnected(false);

      // Track session end
      const sessionEnd = new Date();
      const duration = (sessionEnd - sessionStartRef.current) / 1000; // in seconds

      trackAgentUsage("session_end", {
        session_end: sessionEnd.toISOString(),
        duration: duration,
        total_interactions: interactionCountRef.current,
      });
    };

    socketRef.current = socket;

    // Clean up on unmount
    return () => {
      if (
        socketRef.current &&
        socketRef.current.readyState === WebSocket.OPEN
      ) {
        socketRef.current.close();
      }
    };
  }, [agentType, userId, userType]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || !connected) return;

    const userMessage = input;
    const interactionStart = new Date();

    setLoading(true);
    try {
      // Add user message to history
      setHistory((prev) => [
        ...prev,
        {
          type: "user",
          message: userMessage,
          timestamp: interactionStart,
        },
      ]);

      // Send message to WebSocket
      if (
        socketRef.current &&
        socketRef.current.readyState === WebSocket.OPEN
      ) {
        const message = JSON.stringify({
          type: "message",
          content: userMessage,
          userId: userId,
          userType: userType,
          context: context || {},
          timestamp: interactionStart.toISOString(),
        });
        socketRef.current.send(message);

        // Track individual interaction
        trackAgentUsage("interaction", {
          query: userMessage,
          timestamp: interactionStart.toISOString(),
        });
      } else {
        throw new Error("WebSocket connection is not open");
      }

      setInput("");
    } catch (error) {
      console.error("Agent interaction error:", error);
      setHistory((prev) => [
        ...prev,
        { type: "user", message: userMessage, timestamp: new Date() },
        {
          type: "error",
          message: "Sorry, I encountered an error. Please try again.",
          timestamp: new Date(),
        },
      ]);
      setLoading(false);
    }
  };

  // Auto-scroll to bottom when history changes
  const chatHistoryRef = useRef(null);
  useEffect(() => {
    if (chatHistoryRef.current) {
      chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight;
    }
  }, [history]);

  return (
    <Layout>
      <div className="agent-interaction">
        <header>
          <button onClick={() => navigate(-1)} className="back-button">
            <i className="fas fa-arrow-left"></i> Back
          </button>
          <h2>
            <i className={currentAgent.icon}></i> {currentAgent.title}
          </h2>
          <div className="connection-status">
            <span
              className={`status-indicator ${
                connected ? "connected" : "disconnected"
              }`}
            ></span>
            {connected ? "Connected" : "Disconnected"}
          </div>
        </header>

        <div className="chat-history" ref={chatHistoryRef}>
          {history.map((item, index) => (
            <div key={index} className={`message ${item.type}`}>
              <div className="message-content">{item.message}</div>
              <div className="message-timestamp">
                {item.timestamp
                  ? item.timestamp.toLocaleTimeString()
                  : new Date().toLocaleTimeString()}
              </div>
            </div>
          ))}
          {loading && (
            <div className="message agent loading">
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="input-form">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={currentAgent.placeholder}
            disabled={loading || !connected}
          />
          <button
            type="submit"
            disabled={loading || !input.trim() || !connected}
          >
            <i className="fas fa-paper-plane"></i>
          </button>
        </form>

        {!connected && (
          <div className="connection-warning">
            <i className="fas fa-exclamation-triangle"></i>
            Connection lost. Trying to reconnect...
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AgentInteraction;
